import { Alert, Button, Card, FieldBlock, Input } from '@orbind/ui'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { api, webTokenStorage } from '../lib/api.ts'
import { OrbindLogo } from '../shell/OrbindLogo.tsx'
import './login.css'

/**
 * Conta SEM nenhum workspace cai aqui (SPEC-20260814-2128): apos login com
 * `needsWorkspace` ou apos excluir o ultimo workspace. Cria o primeiro via
 * /onboarding/first-workspace (reautentica com as credenciais recebidas por
 * state — o token do app sempre carrega workspaceId, entao nao ha sessao
 * antes de existir um workspace).
 */
export function SetupWorkspacePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const credenciais = (location.state ?? null) as { email?: string; password?: string } | null

  const [nome, setNome] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)

  // sem credenciais em memoria (refresh, link direto): volta ao login
  if (!credenciais?.email || !credenciais?.password) return <Navigate to="/login" replace />

  async function criar(event: FormEvent) {
    event.preventDefault()
    setCriando(true)
    setErro(null)
    try {
      const resposta = await api.onboarding['first-workspace'].$post({
        json: { email: credenciais!.email!, password: credenciais!.password!, workspaceName: nome.trim() },
      })
      const dados = await resposta.json()
      webTokenStorage.set(dados.token)
      navigate('/home', { replace: true })
    } catch (err) {
      setErro(err instanceof Error && err.message ? err.message : 'Não foi possível criar o workspace.')
    } finally {
      setCriando(false)
    }
  }

  return (
    <div className="ob-root login">
      <section className="login__brand" aria-hidden>
        <div className="login__logo">
          <OrbindLogo size={44} color="#FFFFFF" halo="var(--ob-primary)" />
        </div>
        <h1 className="login__brand-title">Orbind</h1>
        <p className="login__brand-text">Todo trabalho no Orbind vive dentro de um workspace — crie o seu para começar.</p>
      </section>
      <section className="login__panel">
        <Card className="login__card">
          <h2 className="login__title">Crie seu workspace</h2>
          <p className="login__subtitle">Sua conta não está em nenhum workspace. Crie um para entrar.</p>
          <form className="ob-stack" onSubmit={criar} noValidate>
            {erro ? (
              <Alert tone="danger" title="Não foi possível criar">
                {erro}
              </Alert>
            ) : null}
            <FieldBlock label="Nome do workspace" required hint="Ex.: o nome da sua empresa ou do seu time.">
              {({ id }) => <Input id={id} autoFocus required value={nome} onChange={(e) => setNome(e.target.value)} />}
            </FieldBlock>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" loading={criando} disabled={nome.trim().length < 2}>
                Criar workspace e entrar
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login', { replace: true })}>
                Cancelar
              </Button>
            </div>
            <p style={{ fontSize: 12.5, margin: 0, textAlign: 'center' }}>
              <a className="login__link" href="/login">
                Voltar ao login
              </a>
            </p>
          </form>
        </Card>
      </section>
    </div>
  )
}
